import {
  useAuthStore,
  selectGenerationsRemaining,
  selectGenerationsWindow,
} from '@/stores/authStore'
import { tierLabel, tierFor } from '@/lib/tiers'
import { formatGenerationsLeft } from '@/lib/usageFormat'

/**
 * Compact navbar usage display (C4): a tier pill + the generations-left text.
 * Reads ONLY through C1's store slice/selectors. Renders nothing when the user
 * isn't loaded (logged-out / pre-hydrate).
 *
 * Unlimited tiers (pro/commercial) are known from the tier policy alone, so we
 * show "Unlimited" immediately without waiting for the usage slice to hydrate.
 * Metered tiers still show "—" until usage loads.
 *
 * Window-aware (C1): cycle-window (Stripe) users see "left this month", year
 * users "left this year". `formatGenerationsLeft` is the single source of the
 * null===unlimited rule (returns bare "Unlimited"), so the old `short` prop
 * collapse is no longer needed.
 */
export function UsageBadge() {
  const tier = useAuthStore((s) => s.user?.tier ?? null)
  const remaining = useAuthStore(selectGenerationsRemaining)
  const genWindow = useAuthStore(selectGenerationsWindow)
  const usageLoaded = useAuthStore((s) => s.usage !== null)

  if (!tier) return null

  // null == unlimited from the tier policy; lets unlimited plans skip the "—"
  // placeholder before usage hydrates.
  const unlimitedTier = tierFor(tier).yearlyAllowance === null
  const remainingResolved = usageLoaded ? remaining : unlimitedTier ? null : undefined

  // Default display to 'year' until usage hydrates — every pre-Stripe user is
  // year-window, so the fallback can never show wrong copy to a real user.
  const text =
    remainingResolved === undefined
      ? '—'
      : formatGenerationsLeft(remainingResolved, genWindow ?? 'year')

  return (
    <div className="hidden items-center gap-2 sm:flex" aria-label="Subscription usage">
      <span className="bg-sage/15 text-sage inline-block rounded-full px-2.5 py-0.5 text-xs font-medium">
        {tierLabel(tier)}
      </span>
      <span className="text-xs text-gray-500">{text}</span>
    </div>
  )
}
